import { logger } from "@/utils/debug";
import type { AiResponse } from "@/types/ai";
import type { AiAssistant } from "@/types/ai/Assistant";
import { createLanguageInstruction } from "../utils/language";

/**
 * Base Assistant class
 * All assistants extend this class and implement execute and getSystemPrompt
 */
export abstract class Assistant {
  abstract config(): AiAssistant;
  abstract execute(prompt: string, ...args: any[]): Promise<any>;

  static async sendPrompt<T>(
    prompt: string,
    options: AiAssistant = {
      systemPrompt: "",
      model: "gpt-4o-mini",
      temperature: 0.3,
      maxTokens: 500,
      language: "en",
      responseFormat: "text",
    }
  ): Promise<AiResponse<T>> {
    const languageInstruction = createLanguageInstruction(options.language);
    const systemPrompt = `${languageInstruction} ${options.systemPrompt}`;

    const messages = [
      { role: "system" as const, content: systemPrompt.trim() },
      { role: "user" as const, content: prompt.trim() },
    ];

    const requestBody: any = {
      model: options.model,
      messages,
      temperature: options.temperature,
      max_tokens: options.maxTokens,
    };

    // Add response_format for JSON mode if specified
    if (options.responseFormat === "json_object") {
      requestBody.response_format = { type: "json_object" };
    }

    const response = await fetch("/openai", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const errorMessage =
        error.error?.message ||
        error.message ||
        error.error ||
        "AI request failed";
      logger.error(JSON.stringify(error, null, 2), errorMessage);
      throw new Error(errorMessage);
    }

    const result = await response.json();

    const rawContent = result.choices?.[0]?.message?.content;
    if (!rawContent) {
      throw new Error("No content in AI response");
    }

    // Try to extract JSON from markdown code blocks or use content directly
    let aiResponse = rawContent
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/\s*```$/i, "")
      .trim();

    let jsonResponse: AiResponse<T>;
    try {
      jsonResponse = JSON.parse(aiResponse);
    } catch (parseError) {
      logger.warn(
        "[Assistant.sendPrompt] Failed to parse JSON, content:",
        aiResponse
      );

      if (
        !aiResponse.trim().startsWith("{") &&
        !aiResponse.trim().startsWith("[")
      ) {
        return {
          error: `AI returned unexpected format. Expected JSON but got: ${aiResponse.substring(
            0,
            100
          )}${aiResponse.length > 100 ? "..." : ""}`,
        } as AiResponse<T>;
      }

      throw new Error(
        `Failed to parse AI response as JSON: ${
          parseError instanceof Error ? parseError.message : String(parseError)
        }`
      );
    }

    logger.log("[Assistant.sendPrompt] Result:", jsonResponse);
    return jsonResponse;
  }
}
